import type { ReactNode } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useTenant } from "../tenant/TenantContext";
import { can, visibleNavItems, type Capability } from "../tenant/permissions";
import { ROLE_LABELS } from "../api/types";
import { ErrorState, LoadingState } from "./ui";
import { MockBanner } from "./MockBanner";

// Camada de proteção das rotas internas: exige sessão autenticada e um tenant
// ativo escolhido entre os vínculos do usuário.
export function PortalShell() {
  const auth = useAuth();
  const tenant = useTenant();

  if (auth.status === "loading") return <FullPage><LoadingState label="Verificando sessão…" /></FullPage>;
  if (auth.status === "signed_out") return <Navigate to="/login" replace />;
  if (tenant.sessionStatus === "loading" || tenant.sessionStatus === "idle") {
    return <FullPage><LoadingState label="Carregando seus condomínios…" /></FullPage>;
  }
  if (tenant.sessionStatus === "error") {
    return (
      <FullPage>
        <ErrorState error={new Error(tenant.sessionError ?? "Falha ao carregar a sessão.")} onRetry={tenant.refetchSession} />
        <button type="button" className="secondary" onClick={tenant.signOut}>Sair</button>
      </FullPage>
    );
  }
  if (tenant.memberships.length === 0) {
    return (
      <FullPage>
        <div className="login-card">
          <span className="eyebrow">AVA REMOTA</span>
          <h1>Sem condomínios vinculados</h1>
          <p>Sua conta ainda não possui vínculo com nenhum condomínio. Fale com o responsável do seu condomínio ou com a equipe AVA.</p>
          <button type="button" onClick={tenant.signOut}>Sair</button>
        </div>
      </FullPage>
    );
  }
  if (!tenant.activeMembership) return <Navigate to="/selecionar-condominio" replace />;

  const membership = tenant.activeMembership;
  const navItems = visibleNavItems(membership.role);

  return (
    <>
      <MockBanner />
      <div className="shell">
        <aside>
          <div>
            <span className="eyebrow">AVA REMOTA</span>
            <h1>Portal do Condomínio</h1>
            <p className="tenant-name">{membership.tenant_name}</p>
            <p>{ROLE_LABELS[membership.role]}</p>
          </div>
          <nav aria-label="Navegação principal">
            {navItems.map((item) => (
              <NavLink key={item.path} to={item.path} end={item.path === "/"}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <footer>
            <div className="who">
              <strong>{tenant.user?.name}</strong>
              <span>{tenant.user?.email}</span>
            </div>
            {tenant.memberships.length > 1 && (
              <button type="button" onClick={tenant.clearActiveTenant}>
                Trocar de condomínio
              </button>
            )}
            <button type="button" onClick={tenant.signOut}>Sair</button>
          </footer>
        </aside>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </>
  );
}

function FullPage({ children }: { children: ReactNode }) {
  return (
    <>
      <MockBanner />
      <main className="login">
        <div style={{ display: "grid", gap: 12, justifyItems: "center" }}>{children}</div>
      </main>
    </>
  );
}

// Guarda visual por papel. A API continua sendo a autoridade: mesmo que esta
// checagem falhe, o backend nega a operação.
export function RequireCapability({ capability, children }: { capability: Capability; children: ReactNode }) {
  const tenant = useTenant();
  const role = tenant.activeMembership?.role;
  if (!role || !can(role, capability)) {
    return (
      <section className="panel" role="alert">
        <h2>Acesso negado</h2>
        <p>Seu papel neste condomínio não permite acessar esta área.</p>
      </section>
    );
  }
  return <>{children}</>;
}
